import type { Node, Edge } from '@xyflow/react';
import type { HandleType, NodeRegistry } from '../types/registry';
import type { CodeBlockOutput, NodeConfig } from '../types/editor';

export type ResolvedType = {
  name: string;
  tsType: string;
};

const HANDLE_TYPE_TO_TS: Record<HandleType, string> = {
  number: 'number',
  string: 'string',
  boolean: 'boolean',
  object: 'Record<string, any>',
  array: 'any[]',
  any: 'any'
};

function handleTypeToTsType( type: HandleType, tsType?: string ): string {
  if ( tsType ) return tsType;
  return HANDLE_TYPE_TO_TS[type] ?? 'any';
}

export function resolveCodeBlockInputTypes(
  nodeId: string,
  edges: Edge[],
  nodes: Node[],
  registry: NodeRegistry
): ResolvedType[] {
  const incomingEdges = edges.filter(
    ( e ) => e.target === nodeId && e.targetHandle && e.targetHandle !== 'code'
  );

  return incomingEdges.reduce<ResolvedType[]>(( acc, edge ) => {
    const sourceNode = nodes.find(( n ) => n.id === edge.source );
    if ( !sourceNode ) return acc;

    const sourceType = ( sourceNode.data as Record<string, unknown> ).type as string | undefined;
    if ( !sourceType ) return acc;

    const registryEntry = registry[sourceType];
    if ( !registryEntry ) return acc;

    const outputHandle = registryEntry.outputs.find(( o ) => o.name === edge.sourceHandle );
    if ( !outputHandle ) return acc;

    acc.push({
      name: edge.targetHandle!,
      tsType: handleTypeToTsType( outputHandle.type, outputHandle.tsType )
    });

    return acc;
  }, []);
}

export function resolveCodeBlockOutputTypes(
  nodeConfig: NodeConfig
): ResolvedType[] {
  const outputs = nodeConfig?.values?.outputs as CodeBlockOutput[] | undefined;

  if ( !outputs || outputs.length === 0 ) {
    return [{ name: 'output', tsType: 'any' }];
  }

  return outputs.map(( output ) => ({
    name: output.name,
    tsType: HANDLE_TYPE_TO_TS[output.type] ?? 'any'
  }));
}
